import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Home from "./pages/Home";
import Login from "./pages/Login";
import UserProfile from "./pages/UserProfile";
import OBDPage from "./pages/OBDPage";
import MechanicProfile from "./pages/MechanicProfile";
import GoogleWrapper from "./pages/GoogleWrapper";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import ConfirmVoiceBooking from "./pages/ConfirmVoiceBooking";
import VerifyEmailChange from "./pages/VerifyEmailChange";
import CancelEmailChange from "./pages/CancelEmailChange";
import MechanicDashboard from './pages/MechanicDashboard';
import About from "./pages/About";
import Contact from "./pages/Contact";
import Help from "./pages/Help";
import ForgotPassword from "./components/forgotpassword";
import ResetPassword from "./components/resetpassword";
import BookService from "./pages/BookService";
import TOS from "./components/TOS";
import PrivacyPolicy from "./components/PrivacyPolicy";
import SAAS from "./components/SAAS";
import MasterAdminDashboard from "./admin/pages/MasterAdminDashboard";
import AdminDashboard from "./admin/pages/AdminDashboard";
import AdminManagement from "./admin/pages/AdminManagement";
import PricingDashboard from "./admin/pages/PricingDashboard";
import AdminPricing from "./admin/pages/AdminPricing";
import AdminPlatformFee from "./admin/pages/AdminPlatformFee";
import DBManagement from "./admin/pages/DBManagement";
import AdminV2Layout from "./admin/v2/AdminV2Layout";
import OverviewPage from "./admin/v2/pages/OverviewPage";
import FinanceHubPage from "./admin/v2/pages/FinanceHubPage";
import PlaceholderPage from "./admin/v2/pages/PlaceholderPage";
import DevHubPage, { AccessRequestsPage } from "./admin/v2/pages/DevHubPage";
import { VaultLobbyPage, VaultGroupPage } from "./admin/v2/pages/VaultPages";
import UsersAccessPage from "./admin/v2/pages/UsersAccessPage";
import OperationsPage from "./admin/v2/pages/OperationsPage";
import Pricing from "./pages/Pricing";
import GoogleAnalytics from "./components/GoogleAnalytics";
import { Analytics } from '@vercel/analytics/react';
import OBD2Page from "./pages/OBD2Page";
import CalendarPage from "./pages/CalendarPage";
import CalendarEmbed from "./pages/CalendarEmbed";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ServerErrorNoticeProvider } from "./contexts/ServerErrorNoticeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/scrollToTop";

function App() {
  // Environment indicator for all environments
  const getEnvironmentInfo = () => {
    const hostname = window.location.hostname;
    
    // Production domains - no banner
    if (hostname === 'autoshops.com' || 
        hostname === 'www.autoshops.com' || 
        hostname === 'autoshops-frontend.vercel.app' ||
        hostname.includes('git-main')) {
      return null;
    }
    
    // Test/Testing environments (check for -env-test- or -git-test- in URL)
    if (hostname.includes('env-test') || hostname.includes('git-test') || hostname.includes('test.autoshops.com')) {
      return { env: 'Testing Environment', color: '#FF9800', icon: '🧪' };
    }
    
    // Staging/PreProd environments (check for -git-preprod- in URL)
    if (hostname.includes('git-preprod') || hostname.includes('preprod.autoshops.com') || hostname.includes('staging')) {
      return { env: 'Staging Environment', color: '#9C27B0', icon: '🎭' };
    }
    
    // Development (localhost only)
    if (hostname.includes('localhost') || process.env.NODE_ENV === 'development') {
      return { env: 'Development Environment', color: '#2196F3', icon: '🚀' };
    }
    
    return null;
  };

  const envInfo = getEnvironmentInfo();
  
  return (
    <>
      <GoogleAnalytics />
      <Analytics/>
      {envInfo && (
        <div style={{
          background: envInfo.color,
          color: 'white',
          padding: '4px 8px',
          fontSize: '12px',
          textAlign: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999
        }}>
          {envInfo.icon} {envInfo.env}
        </div>
      )}
      <ServerErrorNoticeProvider>
      <SubscriptionProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/obd" element={<OBDPage />} />
          <Route path="/mechanic-profile" element={<MechanicProfile />} />
          <Route path="/google-wrapper" element={<GoogleWrapper />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/confirm-voice-booking/:token" element={<ConfirmVoiceBooking />} />
          <Route path="/verify-email-change/:token" element={<VerifyEmailChange />} />
          <Route path="/cancel-email-change/:cancelToken" element={<CancelEmailChange />} />
          <Route path="/mechanic-dashboard" element={<MechanicDashboard />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/calendar/embed" element={<CalendarEmbed />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Help />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/book-service" element={<BookService />} />
          <Route path="/TOS" element={<TOS />} />
          <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
          <Route path="/SAAS" element={<SAAS />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/admin" element={<MasterAdminDashboard />} />
          <Route path="/admin/shops" element={<AdminDashboard />} />
          <Route path="/admin/management" element={<AdminManagement />} />
          <Route path="/admin/pricing-dashboard" element={<PricingDashboard />} />
          <Route path="/admin/pricing" element={<AdminPricing />} />
          <Route path="/admin/platform-fee" element={<AdminPlatformFee />} />
          <Route 
            path="/admin/db-management" 
            element={
              <ProtectedRoute requireDeveloper>
                <DBManagement />
              </ProtectedRoute>
            } 
          />
          {/* TASK-681 Admin Dashboard Program — parallel /admin/v2 (old /admin stays landing) */}
          <Route path="/admin/v2" element={<AdminV2Layout />}>
            <Route index element={<OverviewPage />} />
            <Route path="finance" element={<FinanceHubPage />} />
            <Route path="finance/fee-program" element={<FinanceHubPage />} />
            <Route path="sales" element={<PlaceholderPage title="Sales & Marketing" />} />
            <Route path="operations" element={<OperationsPage />} />
            <Route
              path="analytics"
              element={
                <PlaceholderPage
                  title="Analytics & Traffic"
                  description="Traffic, SEO, new visitors, time on page, and related insights (Google Analytics and similar). Coming in a later slice."
                />
              }
            />
            <Route path="users" element={<UsersAccessPage />} />
            <Route path="security" element={<PlaceholderPage title="Security & Compliance" />} />
            <Route path="dev" element={<DevHubPage />} />
            <Route path="dev/vault" element={<VaultLobbyPage />} />
            <Route path="dev/vault/:groupKey" element={<VaultGroupPage />} />
            <Route path="dev/access-requests" element={<AccessRequestsPage />} />
          </Route>
          <Route path="/obd2" element={<OBD2Page />} />
          </Routes>
        </Router>
      </SubscriptionProvider>
      </ServerErrorNoticeProvider>
    </>
  );
}

export default App;
